import { PricingTreatmentField } from "@/components/PricingTreatmentField";
import { saveErrorMessage } from "@/lib/saveErrorMessage";
import { MarkupOverrideField } from "@/components/MarkupOverrideField";
import { SectionSelectField } from "@/components/SectionSelectField";
import { pricingTreatment, type PricingTreatment } from "@/lib/quoteChoices";
import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Session } from "@supabase/supabase-js";
import { format } from "date-fns";
import { DatePickerField } from "./DatePickerField";
import { ImageUploadField } from "./ImageUploadField";
import { RichTextField } from "./RichTextField";
import { Sparkles, UtensilsCrossed, Bus, Palmtree, Coffee, Hotel, Compass, Plane, Ticket } from "lucide-react";
import { useDatePickerDefault } from "@/hooks/itinerary-dates";
import { DEFAULT_ITINERARY_SECTIONS, defaultSectionForItem, parseItinerarySections } from "@/lib/itinerarySections";

interface AddCustomItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  session: Session | null;
  leadStartDate?: string | null;
}

// Default placeholder images for custom items
const DEFAULT_IMAGES = [
  { id: "free-time", label: "Free Time", icon: Palmtree, color: "text-blue-500" },
  { id: "meal", label: "Meal", icon: UtensilsCrossed, color: "text-orange-500" },
  { id: "transfer", label: "Transfer", icon: Bus, color: "text-green-500" },
  { id: "other", label: "Other", icon: Coffee, color: "text-purple-500" },
  { id: "accommodation", label: "Accommodation", icon: Hotel, color: "text-cyan-500" },
  { id: "tours", label: "Tours", icon: Compass, color: "text-emerald-500" },
  { id: "flights", label: "Flights", icon: Plane, color: "text-sky-500" },
  { id: "activities", label: "Activities", icon: Ticket, color: "text-pink-500" },
];

export function AddCustomItemDialog({ 
  open, 
  onOpenChange, 
  leadId, 
  session,
  leadStartDate 
}: AddCustomItemDialogProps) {
  const queryClient = useQueryClient();
  // Single source of truth for itinerary default date.
  const defaultDate = useDatePickerDefault(leadStartDate);
  // Tracks explicit user date selection; gates the async adoption effect.
  const userHasPicked = useRef(false);

  const [formData, setFormData] = useState({
    custom_title: "",
    date: defaultDate,
    start_time: "",
    location: "",
    end_time: "",
    custom_description: "",
    manual_price: "",
    manual_image_url: "",
    selectedDefaultImage: "",
    is_background_cost: false,
    section_name: "",
  });

  const { data: layoutSettings } = useQuery({
    queryKey: ["lead-itinerary-layout", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("itinerary_layout, itinerary_sections")
        .eq("id", leadId)
        .single();
      if (error) throw error;
      return data;
    },
  });
  const sectionOptions = [...new Set([
    ...parseItinerarySections(layoutSettings?.itinerary_sections),
    ...DEFAULT_ITINERARY_SECTIONS,
  ])];

  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [priceBasis, setPriceBasis] = useState<"flat" | "per_person">("flat");
  const [people, setPeople] = useState<string>("");
  const { data: leadPaxCount } = useQuery({
    queryKey: ["lead-pax-count", leadId],
    enabled: open && !!leadId,
    queryFn: async () => {
      const { data } = await supabase.from("leads").select("pax_count").eq("id", leadId).maybeSingle();
      return data?.pax_count ?? 1;
    },
  });
  const [pricing, setPricing] = useState<PricingTreatment>("included");
  const [alternativeTo, setAlternativeTo] = useState<string | null>(null);
  const [markupOverride, setMarkupOverride] = useState<string>("");
  const [secondaryImage, setSecondaryImage] = useState<string | null>(null);
  const [image3, setImage3] = useState<string | null>(null);
  const [image4, setImage4] = useState<string | null>(null);

  // Adopt the hook value once it resolves after async lead load,
  // unless the user has already picked a date themselves.
  useEffect(() => {
    if (userHasPicked.current) return;
    if (!defaultDate) return;
    setFormData((prev) => (prev.date ? prev : { ...prev, date: defaultDate }));
  }, [defaultDate]);

  // Reset the "user picked" flag whenever the dialog closes.
  useEffect(() => {
    if (!open) userHasPicked.current = false;
  }, [open]);

  const createCustomItemMutation = useMutation({
    mutationFn: async (data: any) => {
      // Calculate next sort_order to place new items at the bottom
      const { data: maxOrderData } = await supabase
        .from("itinerary_items")
        .select("sort_order")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: false, nullsFirst: false })
        .limit(1)
        .single();
      
      const nextSortOrder = (maxOrderData?.sort_order ?? -1) + 1;
      
      const { error } = await supabase.from("itinerary_items").insert([{
        ...data,
        sort_order: nextSortOrder,
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success("Custom item added to itinerary!");
      onOpenChange(false);
      userHasPicked.current = false;
      // Reset form
      setPriceBasis("flat"); setPeople("");
      setFormData({
        custom_title: "",
        date: defaultDate,
        start_time: "",
    location: "",
        end_time: "",
        custom_description: "",
        manual_price: "",
        manual_image_url: "",
        selectedDefaultImage: "",
        is_background_cost: false,
        section_name: "",
      });
      setMarkupOverride("");
      setUploadedImage(null);
      setSecondaryImage(null);
      setImage3(null);
      setImage4(null);
    },
    onError: (error) => {
      toast.error(saveErrorMessage(error, "Failed to add custom item"));
      console.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!session?.user?.id) {
      toast.error("You must be logged in to add items");
      return;
    }

    if (!formData.custom_title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.is_background_cost && !uploadedImage && !formData.selectedDefaultImage) {
      toast.error("Please upload an image or select a default icon");
      return;
    }

    // Determine final image URL
    const finalImageUrl = formData.is_background_cost && !uploadedImage && !formData.selectedDefaultImage
      ? null
      : (uploadedImage || `default-${formData.selectedDefaultImage}`);

    createCustomItemMutation.mutate({
      lead_id: leadId,
      user_id: session.user.id,
      title: formData.custom_title,
      custom_title: formData.custom_title,
      custom_description: formData.custom_description || null,
      date: formData.date ? format(formData.date, 'yyyy-MM-dd') : null,
      manual_price: formData.manual_price ? parseFloat(formData.manual_price) : 0,
      manual_image_url: finalImageUrl,
      secondary_image_url: secondaryImage || null,
      image_url_3: image3 || null,
      image_url_4: image4 || null,
      item_type: "custom",
      location: formData.location.trim() || null,
      cost: formData.manual_price ? parseFloat(formData.manual_price) : 0,
      quantity: priceBasis === "per_person" ? Math.max(1, parseInt(people) || leadPaxCount || 1) : 1,
      custom_price_basis: priceBasis,
      selected_time_slot: formData.start_time ? {
        label: "Custom",
        start: formData.start_time,
        end: formData.end_time || formData.start_time,
      } : null,
      is_background_cost: formData.is_background_cost,
      pricing_treatment: pricing === "alternative" && !alternativeTo ? "included" : pricing,
      alternative_to_item_id: pricing === "alternative" ? alternativeTo : null,
      markup_percentage:
        markupOverride.trim() === "" || !Number.isFinite(parseFloat(markupOverride))
          ? null
          : parseFloat(markupOverride),
      section_name: formData.section_name.trim() || defaultSectionForItem({ item_type: "custom" } as any),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Create Custom Item
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {layoutSettings?.itinerary_layout === "sections" && (
            <SectionSelectField
              id="custom_section_name"
              leadId={leadId}
              value={formData.section_name}
              onChange={(v) => setFormData((prev) => ({ ...prev, section_name: v }))}
              options={sectionOptions}
            />
          )}
          <div className="space-y-2">
            <Label htmlFor="custom_title">Title *</Label>
            <Input
              id="custom_title"
              value={formData.custom_title}
              onChange={(e) => setFormData({ ...formData, custom_title: e.target.value })}
              placeholder="e.g., Free Time at the Beach"
              required
            />
          </div>

          <DatePickerField
            label="Date"
            value={formData.date}
            onChange={(date) => {
              userHasPicked.current = true;
              setFormData({ ...formData, date });
            }}
            defaultMonth={defaultDate}
          />

          <div className="space-y-2">
            <Label htmlFor="custom_location">Location</Label>
            <Input
              id="custom_location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Leave blank to use the hotel's city for that date"
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_time">Start Time</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_time">End Time</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom_description">Description</Label>
            <RichTextField
              id="custom_description"
              value={formData.custom_description}
              onChange={(html) => setFormData((prev) => ({ ...prev, custom_description: html }))}
              placeholder="Describe this activity or time block..."
            />
          </div>

          <div className="space-y-2">
            <Label>Pricing basis</Label>
            <div className="flex gap-2">
              {(["flat", "per_person"] as const).map((b) => (
                <Button key={b} type="button" size="sm" variant={priceBasis === b ? "default" : "outline"}
                  onClick={() => { setPriceBasis(b); if (b === "per_person" && !people) setPeople(String(leadPaxCount ?? 1)); }}>
                  {b === "flat" ? "Flat total" : "Per person"}
                </Button>
              ))}
            </div>
            <Label htmlFor="manual_price">{priceBasis === "per_person" ? "Price per person" : "Cost/Price"}</Label>
            <Input
              id="manual_price"
              type="number"
              step="0.01"
              min="0"
              value={formData.manual_price}
              onChange={(e) => setFormData({ ...formData, manual_price: e.target.value })}
              placeholder="0.00"
            />
            {priceBasis === "per_person" && (
              <>
                <Label htmlFor="custom_people">Number of people</Label>
                <Input id="custom_people" type="number" min={1} value={people} onChange={(e) => setPeople(e.target.value)} />
                <p className="text-sm text-muted-foreground">
                  {(parseFloat(formData.manual_price) || 0).toLocaleString()} × {parseInt(people) || 0} people ={" "}
                  <span className="font-semibold text-foreground">{((parseFloat(formData.manual_price) || 0) * (parseInt(people) || 0)).toLocaleString()}</span>
                </p>
              </>
            )}
          </div>

          <PricingTreatmentField
            leadId={leadId}
            treatment={pricing}
            alternativeTo={alternativeTo}
            onChange={(t, a) => { setPricing(t); setAlternativeTo(a); }}
          />

          <MarkupOverrideField
            value={markupOverride}
            onChange={setMarkupOverride}
            netTotal={(parseFloat(formData.manual_price) || 0) * (priceBasis === "per_person" ? (parseInt(people) || 1) : 1)}
          />

          <div className="flex items-start justify-between gap-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="space-y-1">
              <Label htmlFor="custom_is_background_cost" className="text-sm font-medium">
                Background cost
              </Label>
              <p className="text-xs text-muted-foreground">
                Hide this item from the itinerary on every document (e.g. guide fees, vehicle repositioning, gratuity pool). Its cost is still factored into the total. It appears as a line in the Quote PDF programme breakdown only when "Show line item breakdown" is on.
              </p>
            </div>
            <Switch
              id="custom_is_background_cost"
              checked={formData.is_background_cost}
              onCheckedChange={(checked) => setFormData({ ...formData, is_background_cost: checked })}
            />
          </div>

          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">
                Hero Image {formData.is_background_cost ? "(optional for background costs)" : "*"}
              </Label>
              <ImageUploadField
                value={uploadedImage}
                onChange={(url) => {
                  setUploadedImage(url);
                  setFormData({ ...formData, selectedDefaultImage: "" });
                }}
                entityType="product"
                required={!formData.selectedDefaultImage}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or select a default icon
                </span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {DEFAULT_IMAGES.map((img) => {
                const Icon = img.icon;
                const isSelected = formData.selectedDefaultImage === img.id;
                return (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, selectedDefaultImage: img.id });
                      setUploadedImage(null);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <Icon className={`h-8 w-8 ${isSelected ? "text-primary" : img.color}`} />
                    <span className="text-xs font-medium">{img.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Additional Images (Optional) */}
          <div className="space-y-2">
            <Label className="mb-2 block">Additional Images (Optional, up to 3)</Label>
            <p className="text-xs text-muted-foreground">Shown below the hero image in quotes</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ImageUploadField value={secondaryImage} onChange={(url) => setSecondaryImage(url)} entityType="product" />
              <ImageUploadField value={image3} onChange={(url) => setImage3(url)} entityType="product" />
              <ImageUploadField value={image4} onChange={(url) => setImage4(url)} entityType="product" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createCustomItemMutation.isPending}>
              {createCustomItemMutation.isPending ? "Adding..." : "Add Custom Item"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
